import React, { useState, useEffect } from "react";
import axios from "axios";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Swal from "sweetalert2";
import "bootstrap-icons/font/bootstrap-icons.css";

const Bank = () => {
  const [loading, setLoading] = useState(false);
  const [banks, setBanks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const guestId = localStorage.getItem('userid');

  const [currentBank, setCurrentBank] = useState({
    id: "",
    userId: guestId,
    bankName: "",
    accountNumber: "",
    accountHolderName: "",
    ifscCode: "",
    balance: 0,
    status: "1",
    type: "admin",
  });

  useEffect(() => {
    loadBanks();
  }, []);

  const loadBanks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}admin/get-bank-list`);

      // Filter banks with type "admin"
      const adminBanks = response.data.banks?.filter(bank => bank.type === "admin") || [];

      setBanks(adminBanks);
    } catch (error) {
      console.error("❌ Error fetching banks:", error);
      // Swal.fire("Error!", "Failed to fetch bank details.", "error");
    } finally {
      setLoading(false);
    }
  };


  const handleAddBank = () => {
    setIsEditing(false);
    setCurrentBank({
      id: "",
      userId: guestId,
      bankName: "",
      accountNumber: "",
      accountHolderName: "",
      ifscCode: "",
      balance: 0,
      status: "1",
      type: "admin",
    });
    setShowModal(true);
  };

  const handleEditBank = (bank) => {
    setIsEditing(true);
    setCurrentBank(bank);
    setShowModal(true);
  };

  const handleDeleteBank = async (bankId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const response = await axios.delete(`${process.env.REACT_APP_API_URL}admin/delete-bank-details/${bankId}`);
          if (response.data.success) {
            Swal.fire("Deleted!", "Bank account has been deleted.", "success");
            loadBanks();
          } else {
            Swal.fire("Error!", response.data.message, "error");
          }
        } catch (error) {
          console.error("Error deleting bank account:", error);
          Swal.fire("Error!", "Failed to delete bank account.", "error");
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleSaveBank = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = isEditing
        ? `${process.env.REACT_APP_API_URL}admin/update-bank-details/${currentBank._id}`
        : `${process.env.REACT_APP_API_URL}admin/add-bank-details`;
      const method = isEditing ? "put" : "post";
      const response = await axios[method](url, currentBank);
      if (response.data.success) {
        Swal.fire("Success!", `Bank account ${isEditing ? "updated" : "added"} successfully.`, "success");
        setShowModal(false);
        loadBanks();
      } else {
        Swal.fire("Error!", response.data.message, "error");
      }
    } catch (error) {
      console.error(`Error ${isEditing ? "updating" : "adding"} bank account:`, error);
      Swal.fire("Error!", `Failed to ${isEditing ? "update" : "add"} bank account.`, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentBank((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleToggleStatus = async (bank) => {
    setLoading(true);
    try {
      const newStatus = bank.status === "1" ? "0" : "1"; // Toggle between 1 and 0
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}admin/update-bank-status/${bank._id}`,
        { status: newStatus }
      );

      if (response.data.success) {
        Swal.fire("Updated!", "Bank status updated successfully.", "success");
        loadBanks(); // Refresh the bank list
      } else {
        Swal.fire("Error!", response.data.message, "error");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      Swal.fire("Error!", "Failed to update status.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyBank = (bank) => {
    const bankDetails = `
      Bank Name: ${bank.bankName}
      Account Number: ${bank.accountNumber}
      Account Holder Name: ${bank.accountHolderName}
      IFSC Code: ${bank.ifscCode}
      Status: ${bank.status === "1" ? "Active" : "Inactive"}
    `;

    navigator.clipboard
      .writeText(bankDetails)
      .then(() => {
        alert("Bank details copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy bank details:", err);
        alert("Failed to copy bank details.");
      });
  };

  return (
    <div className="profile-container">
      <div className="table-responsive tablemargin">
        <p className="text-center mt-2 requesthistory">Bank Accounts</p>
        <Button variant="primary" onClick={handleAddBank} className="mb-3 addbankbutton">
          Add Bank Account
        </Button>

        {loading ? (
          <div className="text-center my-3">
            <Spinner animation="border" role="status" />
            <p>Loading...</p>
          </div>
        ) : (
          <Table striped bordered hover className="tablehistory position-relative">
            <thead>
              <tr className="tablehead text-center">
                <th>#</th>
                <th>Bank Name</th>
                <th>Account Number</th>
                <th>Account Holder</th>
                <th>IFSC Code</th>
                {/* <th>Balance</th> */}
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className="tbodyhistroy text-center">
              {banks.length > 0 ? (
                banks.map((bank, index) => (
                  <tr className="text-center" key={bank.id}>
                    <td>{index + 1}</td>
                    <td>{bank.bankName}</td>
                    <td>{bank.accountNumber}</td>
                    <td>{bank.accountHolderName}</td>
                    <td>{bank.ifscCode}</td>
                    {/* <td>{bank.balance}</td> */}
                    <td>
                      <Button
                        variant={bank.status === "1" ? "success" : "secondary"}
                        size="sm"
                        onClick={() => handleToggleStatus(bank)}
                      >
                        {bank.status === "1" ? "Active" : "Inactive"}
                      </Button>
                    </td>
                    <td>
                      <Button
                        className="buttonAction me-1"
                        variant="primary"
                        size="sm"
                        onClick={() => handleCopyBank(bank)} 
                        title="Copy Bank Details"
                      >
                        <i className="bi bi-copy"></i>
                      </Button>
                      <Button
                        className="buttonAction"
                        variant="warning"
                        size="sm"
                        onClick={() => handleEditBank(bank)}
                        title="Edit Bank"
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>{" "}
                      <Button
                        className="buttonAction"
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteBank(bank._id)}
                        title="Delete Bank"
                      >
                        <i className="bi bi-trash"></i>
                      </Button>

                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center nodataavl">
                    No bank accounts available
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? "Edit Bank Account" : "Add Bank Account"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSaveBank}>
            <Form.Group className="mb-3">
              <Form.Label>Bank Name</Form.Label>
              <Form.Control
                type="text"
                name="bankName"
                value={currentBank.bankName}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Account Number</Form.Label>
              <Form.Control
                type="text"
                name="accountNumber"
                value={currentBank.accountNumber}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Account Holder Name</Form.Label>
              <Form.Control
                type="text"
                name="accountHolderName"
                value={currentBank.accountHolderName}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>IFSC Code</Form.Label>
              <Form.Control
                type="text"
                name="ifscCode"
                value={currentBank.ifscCode}
                onChange={handleChange}
                required
              />
            </Form.Group>



            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" /> : "Save"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

    </div>
  );
};

export default Bank;

